"use client";

import { FetchMenubar } from "@/fetch-ui/FetchMenubar";
import type { RoomType } from "@/lib/utils/accommodations";
import { Plus } from "lucide-react";

interface AddRoomTypeMenuProps {
  /** Types not currently shown — these are the menu options. */
  availableTypes: ReadonlyArray<RoomType>;
  /** Invoked when the planner picks a type to add. */
  onSelect: (type: RoomType) => void;
}

/**
 * Trigger button + dropdown menu for adding a new room-type column to the
 * Rooms section table. Disabled when every type is already visible.
 *
 * The trigger is a native `<button>` (not `FetchButton`) because Radix's
 * `Menubar.Trigger asChild` requires the child to forward refs and accept
 * arbitrary props — `FetchButton` is a plain function component that does
 * neither, so the menu wouldn't open if we used it here.
 *
 * @param props - Available types to offer + the select handler.
 * @returns The trigger button (with menu attached when types are available).
 */
export const AddRoomTypeMenu = ({ availableTypes, onSelect }: AddRoomTypeMenuProps) => {
  const buttonBase =
    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors";

  if (availableTypes.length === 0) {
    return (
      <button type="button" disabled className={`${buttonBase} cursor-not-allowed opacity-50`}>
        <Plus className="size-4" aria-hidden />
        Add room type
      </button>
    );
  }

  return (
    <FetchMenubar
      trigger={
        <button
          type="button"
          className={`${buttonBase} hover:bg-gray-100 hover:text-gray-900 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900`}
        >
          <Plus className="size-4" aria-hidden />
          Add room type
        </button>
      }
      items={availableTypes.map((type) => ({
        label: type,
        onClick: () => onSelect(type),
      }))}
      align="end"
      side="bottom"
    />
  );
};
